import { AccessTab } from './AccessTab';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  assignRepository,
  fetchAdminAudit,
  fetchAdminPeople,
  fetchAdminRepositories,
  fetchAdminSync,
  requestSync,
  updatePerson,
  type Me,
} from '../api';
import {
  Badge,
  Empty,
  ErrorBox,
  Loading,
  RelativeTime,
  Section,
  Table,
  Value,
} from '../components/ui';

const TABS = [
  { id: 'repositories', label: 'Repositorios' },
  { id: 'people', label: 'Personas' },
  { id: 'access', label: 'Accesos' },
  { id: 'sync', label: 'Sincronización' },
  { id: 'audit', label: 'Auditoría' },
] as const;

export function AdminPage({ me }: { me: Me }): JSX.Element {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'repositories';
  const isAdmin = me.role === 'admin';

  return (
    <>
      <h1 className="mb-1 text-lg font-semibold">Administración</h1>
      <p className="mb-4 text-[12px] text-[var(--color-ink-muted)]">
        {isAdmin
          ? 'Cada cambio queda registrado en la auditoría con su antes y su después.'
          : 'Estás como observador: puedes ver todo, pero no modificar nada.'}
      </p>

      <div className="mb-5 flex flex-wrap gap-1 border-b border-[var(--color-border-subtle)]">
        {TABS.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => setParams({ tab: option.id })}
            className={`-mb-px border-b-2 px-3 py-1.5 text-[13px] ${
              tab === option.id
                ? 'border-[var(--color-ink)] font-medium'
                : 'border-transparent text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {tab === 'repositories' && <RepositoriesTab isAdmin={isAdmin} />}
      {tab === 'people' && <PeopleTab isAdmin={isAdmin} />}
      {tab === 'access' && me && <AccessTab me={me} />}
      {tab === 'sync' && <SyncTab isAdmin={isAdmin} />}
      {tab === 'audit' && <AuditTab />}
    </>
  );
}

// ─── Repositorios ───────────────────────────────────────────────────────────

function RepositoriesTab({ isAdmin }: { isAdmin: boolean }): JSX.Element {
  const client = useQueryClient();
  const query = useQuery({ queryKey: ['admin-repositories'], queryFn: fetchAdminRepositories });

  const assign = useMutation({
    mutationFn: (input: { repoId: number; projectSlug: string | null }) =>
      assignRepository(input.repoId, input.projectSlug),
    onSuccess: () => client.invalidateQueries(),
  });

  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorBox error={query.error} />;

  const { repositories, unmanagedCodebases, projects } = query.data;
  const orphans = repositories.filter((repo) => repo.projectSlug === null);

  return (
    <>
      {unmanagedCodebases.filter((codebase) => !codebase.resolvedAt).length > 0 && (
        <Section
          title="Código en producción sin repositorio remoto"
          subtitle="Riesgo R2 de la auditoría: la única copia está en el disco de esta VPS"
        >
          <Table
            head={['Ruta', 'Sirve', 'Git local', 'Commits', 'Último commit', 'Proyecto', 'Motivo']}
          >
            {unmanagedCodebases.map((codebase) => (
              <tr
                key={codebase.slug}
                className={`border-b border-[var(--color-border-subtle)] last:border-0 ${
                  codebase.resolvedAt ? 'opacity-50' : ''
                }`}
              >
                <td className="px-3 py-2 font-mono text-[11px]">{codebase.vpsPath}</td>
                <td className="px-3 py-2 text-[11px]">
                  {codebase.servesUrl ? (
                    <a
                      href={codebase.servesUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:underline"
                    >
                      {codebase.servesUrl.replace('https://', '')}
                    </a>
                  ) : (
                    <Value reason="sin URL registrada">{null}</Value>
                  )}
                </td>
                <td className="px-3 py-2">
                  {codebase.hasLocalGit ? (
                    <Badge tone="warn">sí</Badge>
                  ) : (
                    <Badge tone="bad">no</Badge>
                  )}
                </td>
                <td className="px-3 py-2 tabular-nums">
                  <Value reason="no es un repositorio git">{codebase.localCommits}</Value>
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-[11px]">
                  <RelativeTime iso={codebase.lastCommitAt} />
                </td>
                <td className="px-3 py-2 text-[11px]">
                  {codebase.projectSlug ? (
                    <Link to={`/projects/${codebase.projectSlug}`} className="hover:underline">
                      {codebase.projectSlug}
                    </Link>
                  ) : (
                    <Value reason="sin proyecto asignado">{null}</Value>
                  )}
                </td>
                <td className="px-3 py-2 text-[11px] text-[var(--color-ink-muted)]">
                  {codebase.reason}
                </td>
              </tr>
            ))}
          </Table>
        </Section>
      )}

      {orphans.length > 0 && (
        <Section
          title="Repositorios sin proyecto"
          subtitle="Aparecen en los totales del espejo pero no en ningún proyecto"
        >
          <ul className="flex flex-wrap gap-1.5">
            {orphans.map((repo) => (
              <li key={repo.id}>
                <Badge tone="warn">{repo.fullName}</Badge>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Section title="Repositorios espejados" subtitle={`${repositories.length} en total`}>
        <Table
          head={['Repositorio', 'Visib.', 'Rama', 'Commits', 'Ramas', 'Sincronizado', 'Proyecto']}
        >
          {repositories.map((repo) => (
            <tr
              key={repo.id}
              className="border-b border-[var(--color-border-subtle)] last:border-0"
            >
              <td className="px-3 py-2">
                {repo.htmlUrl ? (
                  <a
                    href={repo.htmlUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:underline"
                  >
                    {repo.fullName}
                  </a>
                ) : (
                  repo.fullName
                )}
                {repo.isArchived && (
                  <span className="ml-1.5">
                    <Badge>archivado</Badge>
                  </span>
                )}
              </td>
              <td className="px-3 py-2 text-[11px]">
                {repo.visibility === 'unmanaged' ? (
                  <Badge tone="bad">no gestionado</Badge>
                ) : (
                  repo.visibility
                )}
              </td>
              <td className="px-3 py-2 font-mono text-[11px]">{repo.defaultBranch}</td>
              <td className="px-3 py-2 tabular-nums">{repo.commits}</td>
              <td className="px-3 py-2 tabular-nums">{repo.branches}</td>
              <td className="whitespace-nowrap px-3 py-2 text-[11px]">
                <RelativeTime iso={repo.lastSyncAt} />
              </td>
              <td className="px-3 py-2">
                {isAdmin ? (
                  <select
                    value={repo.projectSlug ?? ''}
                    disabled={assign.isPending}
                    onChange={(event) =>
                      assign.mutate({
                        repoId: repo.id,
                        projectSlug: event.target.value === '' ? null : event.target.value,
                      })
                    }
                    className="rounded border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-1.5 py-0.5 text-[11px]"
                  >
                    <option value="">— sin proyecto —</option>
                    {projects.map((project) => (
                      <option key={project.slug} value={project.slug}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="text-[11px]">{repo.projectName ?? '—'}</span>
                )}
              </td>
            </tr>
          ))}
        </Table>
        {assign.isError && (
          <div className="mt-2">
            <ErrorBox error={assign.error} />
          </div>
        )}
      </Section>
    </>
  );
}

// ─── Personas ───────────────────────────────────────────────────────────────

function PeopleTab({ isAdmin }: { isAdmin: boolean }): JSX.Element {
  const client = useQueryClient();
  const query = useQuery({ queryKey: ['admin-people'], queryFn: fetchAdminPeople });
  const [claiming, setClaiming] = useState<string | null>(null);

  const claim = useMutation({
    mutationFn: (input: { personId: number; gitEmails: string[] }) =>
      updatePerson(input.personId, { gitEmails: input.gitEmails }),
    onSuccess: async () => {
      setClaiming(null);
      await client.invalidateQueries();
    },
  });

  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorBox error={query.error} />;

  const { people, identities } = query.data;
  const unmatched = identities.filter((identity) => !identity.matched);

  return (
    <>
      <Section title="Personas" subtitle="La atribución de commits se resuelve por los correos git">
        <Table head={['Nombre', 'GitHub', 'Rol', 'Correos git', 'Commits', 'Proyectos']}>
          {people.map((person) => (
            <tr
              key={person.id}
              className="border-b border-[var(--color-border-subtle)] last:border-0"
            >
              <td className="px-3 py-2 font-medium">{person.displayName}</td>
              <td className="px-3 py-2 text-[12px]">
                <Value reason="sin cuenta de GitHub asociada">{person.githubLogin}</Value>
              </td>
              <td className="px-3 py-2">
                <Badge tone={person.role === 'admin' ? 'warn' : 'neutral'}>{person.role}</Badge>
              </td>
              <td className="px-3 py-2 font-mono text-[11px] leading-relaxed">
                {person.gitEmails.join(', ')}
              </td>
              <td className="px-3 py-2 tabular-nums">{person.commits.toLocaleString('es-ES')}</td>
              <td className="px-3 py-2 tabular-nums">{person.ownsProjects}</td>
            </tr>
          ))}
        </Table>
      </Section>

      <Section
        title="Identidades git sin persona"
        subtitle={
          unmatched.length === 0
            ? 'Todas las identidades están reclamadas.'
            : 'Sus commits no se atribuyen a nadie. Reclamarla es una decisión humana: el portal no la toma solo.'
        }
      >
        {unmatched.length === 0 ? (
          <Empty>Nada pendiente.</Empty>
        ) : (
          <Table
            head={['Correo', 'Nombre en los commits', 'Commits', 'Repos', 'Desde', 'Hasta', '']}
          >
            {unmatched.map((identity) => (
              <tr
                key={identity.email}
                className="border-b border-[var(--color-border-subtle)] last:border-0"
              >
                <td className="px-3 py-2 font-mono text-[11px]">{identity.email}</td>
                <td className="px-3 py-2 text-[12px]">{identity.nameSample ?? '—'}</td>
                <td className="px-3 py-2 tabular-nums">
                  {identity.commits.toLocaleString('es-ES')}
                </td>
                <td className="px-3 py-2 tabular-nums">{identity.repos}</td>
                <td className="whitespace-nowrap px-3 py-2 text-[11px]">
                  <RelativeTime iso={identity.firstCommitAt} />
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-[11px]">
                  <RelativeTime iso={identity.lastCommitAt} />
                </td>
                <td className="px-3 py-2">
                  {isAdmin &&
                    (claiming === identity.email ? (
                      <select
                        autoFocus
                        defaultValue=""
                        onChange={(event) => {
                          const person = people.find(
                            (row) => String(row.id) === event.target.value,
                          );
                          if (person) {
                            claim.mutate({
                              personId: person.id,
                              gitEmails: [...person.gitEmails, identity.email],
                            });
                          }
                        }}
                        className="rounded border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-1.5 py-0.5 text-[11px]"
                      >
                        <option value="">elige persona…</option>
                        {people.map((person) => (
                          <option key={person.id} value={person.id}>
                            {person.displayName}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setClaiming(identity.email)}
                        className="text-[11px] underline opacity-70 hover:opacity-100"
                      >
                        asignar
                      </button>
                    ))}
                </td>
              </tr>
            ))}
          </Table>
        )}
        {claim.isError && (
          <div className="mt-2">
            <ErrorBox error={claim.error} />
          </div>
        )}
      </Section>
    </>
  );
}

// ─── Sincronización ─────────────────────────────────────────────────────────

function SyncTab({ isAdmin }: { isAdmin: boolean }): JSX.Element {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ['admin-sync'],
    queryFn: fetchAdminSync,
    refetchInterval: 30_000,
  });

  const enqueue = useMutation({
    mutationFn: (mode: 'full' | 'incremental') => requestSync({ mode }),
    onSuccess: () => client.invalidateQueries(),
  });

  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorBox error={query.error} />;

  const { state, runs, queue, webhooks, webhookSummary } = query.data;
  const failing = state.filter((row) => row.failures > 0);
  const unavailable = state.filter((row) => row.skippedReason !== null);
  const withEtag = state.filter((row) => row.hasEtag).length;

  return (
    <>
      <Section
        title="Resumen"
        action={
          isAdmin ? (
            <div className="flex gap-1.5">
              <button
                type="button"
                disabled={enqueue.isPending}
                onClick={() => enqueue.mutate('incremental')}
                className="rounded border border-[var(--color-border-subtle)] px-2.5 py-1 text-[12px] disabled:opacity-50"
              >
                Sincronizar (incremental)
              </button>
              <button
                type="button"
                disabled={enqueue.isPending}
                onClick={() => {
                  if (
                    window.confirm('Resincronización completa de todos los repositorios. ¿Seguir?')
                  ) {
                    enqueue.mutate('full');
                  }
                }}
                className="rounded border border-[var(--color-border-subtle)] px-2.5 py-1 text-[12px] disabled:opacity-50"
              >
                Resincronizar todo
              </button>
            </div>
          ) : undefined
        }
      >
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Metric label="Recursos" value={state.length} />
          <Metric
            label="Con fallos"
            value={failing.length}
            tone={failing.length > 0 ? 'bad' : 'good'}
          />
          <Metric label="No disponibles" value={unavailable.length} />
          <Metric label="Con ETag" value={withEtag} />
        </div>
        {enqueue.isSuccess && (
          <p className="mt-2 text-[12px] text-emerald-700 dark:text-emerald-400">
            {enqueue.data.enqueued} trabajo(s) encolado(s) en {enqueue.data.queue}. El worker los
            recogerá en unos segundos.
          </p>
        )}
        {enqueue.isError && (
          <div className="mt-2">
            <ErrorBox error={enqueue.error} />
          </div>
        )}
      </Section>

      {failing.length > 0 && (
        <Section title="Recursos con fallos">
          <Table head={['Repositorio', 'Recurso', 'Fallos', 'Error']}>
            {failing.map((row) => (
              <tr
                key={`${row.repo}-${row.resource}`}
                className="border-b border-[var(--color-border-subtle)] last:border-0"
              >
                <td className="px-3 py-2 text-[12px]">{row.repo}</td>
                <td className="px-3 py-2 font-mono text-[11px]">{row.resource}</td>
                <td className="px-3 py-2 tabular-nums text-red-700 dark:text-red-400">
                  {row.failures}
                </td>
                <td className="px-3 py-2 text-[11px]">{row.lastError}</td>
              </tr>
            ))}
          </Table>
        </Section>
      )}

      <Section title="Ejecuciones recientes">
        {runs.length === 0 ? (
          <Empty>Sin ejecuciones registradas.</Empty>
        ) : (
          <Table head={['Cuándo', 'Objetivo', 'Modo', 'Estado', 'Peticiones', 'Cuota restante']}>
            {runs.map((run) => (
              <tr
                key={run.id}
                className="border-b border-[var(--color-border-subtle)] last:border-0"
              >
                <td className="whitespace-nowrap px-3 py-2 text-[11px]">
                  <RelativeTime iso={run.startedAt} />
                </td>
                <td className="px-3 py-2 text-[12px]">{run.target ?? '—'}</td>
                <td className="px-3 py-2 text-[11px]">{run.mode}</td>
                <td className="px-3 py-2">
                  <Badge
                    tone={run.status === 'ok' ? 'good' : run.status === 'failed' ? 'bad' : 'warn'}
                  >
                    {run.status}
                  </Badge>
                </td>
                <td className="px-3 py-2 tabular-nums">{run.apiCalls ?? '—'}</td>
                <td className="px-3 py-2 tabular-nums">{run.rateRemaining ?? '—'}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section title="Cola de trabajos">
        {queue.length === 0 ? (
          <Empty>La cola está vacía.</Empty>
        ) : (
          <ul className="flex flex-wrap gap-1.5">
            {queue.map((row) => (
              <li key={`${row.name}-${row.state}`}>
                <Badge
                  tone={
                    row.state === 'failed' ? 'bad' : row.state === 'active' ? 'warn' : 'neutral'
                  }
                >
                  {row.name} · {row.state}: {row.count}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title="Entregas de webhook"
        subtitle={
          webhooks.length === 0
            ? 'Todavía no ha llegado ninguna entrega'
            : `Últimas ${webhooks.length} · ${webhookSummary.failed} con error, ${webhookSummary.pending} sin procesar`
        }
      >
        {webhooks.length === 0 ? (
          <Empty>
            No hay entregas registradas. Si la GitHub App ya está instalada con webhook, revisa la
            pestaña «Recent Deliveries» de su configuración: allí se ve si GitHub obtiene respuesta.
          </Empty>
        ) : (
          <Table
            head={['Recibida', 'Evento', 'Repositorio', 'Estado', 'Intentos', 'Latencia', 'Error']}
          >
            {webhooks.map((hook) => (
              <tr key={hook.deliveryId} className="border-t border-slate-100 dark:border-slate-800">
                <td className="whitespace-nowrap px-3 py-2 text-slate-500">
                  {new Date(hook.receivedAt).toLocaleString('es-ES')}
                </td>
                <td className="px-3 py-2 font-medium">
                  {hook.event}
                  {hook.action ? <span className="text-slate-500">.{hook.action}</span> : null}
                </td>
                <td className="px-3 py-2 text-slate-500">{hook.repo ?? '—'}</td>
                <td className="px-3 py-2">
                  <Badge
                    tone={
                      hook.status === 'failed'
                        ? 'bad'
                        : hook.status === 'pending'
                          ? 'warn'
                          : hook.status === 'done'
                            ? 'good'
                            : 'neutral'
                    }
                  >
                    {hook.status}
                  </Badge>
                </td>
                <td className="px-3 py-2 tabular-nums">{hook.attempts}</td>
                {/* null, no cero: una entrega sin procesar no tiene latencia medida. */}
                <td className="px-3 py-2 tabular-nums text-slate-500">
                  {hook.latencyMs === null ? '—' : `${(hook.latencyMs / 1000).toFixed(1)} s`}
                </td>
                <td className="max-w-md px-3 py-2 text-xs text-red-700 dark:text-red-400">
                  {hook.lastError ? hook.lastError.slice(0, 160) : ''}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section
        title="Estado por recurso"
        subtitle="«No disponible» dice por qué falta el dato, en lugar de mostrar cero"
      >
        <Table head={['Repositorio', 'Recurso', 'Elementos', 'Última vez', 'ETag', 'Nota']}>
          {state.map((row) => (
            <tr
              key={`${row.repo}-${row.resource}`}
              className="border-b border-[var(--color-border-subtle)] last:border-0"
            >
              <td className="px-3 py-2 text-[11px]">{row.repo}</td>
              <td className="px-3 py-2 font-mono text-[11px]">{row.resource}</td>
              <td className="px-3 py-2 tabular-nums">{row.items ?? 0}</td>
              <td className="whitespace-nowrap px-3 py-2 text-[11px]">
                <RelativeTime iso={row.lastSuccessAt} />
              </td>
              <td className="px-3 py-2 text-[11px]">{row.hasEtag ? 'sí' : '—'}</td>
              <td className="px-3 py-2 text-[11px] text-[var(--color-ink-muted)]">
                {row.skippedReason ?? ''}
              </td>
            </tr>
          ))}
        </Table>
      </Section>
    </>
  );
}

// ─── Auditoría ──────────────────────────────────────────────────────────────

function AuditTab(): JSX.Element {
  const query = useQuery({ queryKey: ['admin-audit'], queryFn: () => fetchAdminAudit(200) });

  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorBox error={query.error} />;
  if (query.data.length === 0) return <Empty>Sin entradas de auditoría.</Empty>;

  return (
    <Section
      title="Registro de auditoría"
      subtitle="Quién cambió qué, con el valor anterior y el nuevo"
    >
      <Table head={['Cuándo', 'Quién', 'Acción', 'Entidad', 'Antes → Después']}>
        {query.data.map((entry) => (
          <tr
            key={entry.id}
            className="border-b border-[var(--color-border-subtle)] last:border-0 align-top"
          >
            <td className="whitespace-nowrap px-3 py-2 text-[11px]">
              <RelativeTime iso={entry.at} />
            </td>
            <td className="px-3 py-2 text-[12px]">{entry.actorLogin ?? '—'}</td>
            <td className="px-3 py-2">
              <Badge
                tone={
                  entry.action === 'login_denied'
                    ? 'bad'
                    : entry.action.startsWith('update') || entry.action.startsWith('assign')
                      ? 'warn'
                      : 'neutral'
                }
              >
                {entry.action}
              </Badge>
            </td>
            <td className="px-3 py-2 text-[11px] text-[var(--color-ink-muted)]">
              {entry.entityType}
              {entry.entityId !== null ? ` #${entry.entityId}` : ''}
            </td>
            <td className="px-3 py-2 font-mono text-[10px] leading-tight">
              {entry.before !== null && (
                <div className="text-red-700 dark:text-red-400">
                  − {JSON.stringify(entry.before)}
                </div>
              )}
              {entry.after !== null && (
                <div className="text-emerald-700 dark:text-emerald-400">
                  + {JSON.stringify(entry.after)}
                </div>
              )}
            </td>
          </tr>
        ))}
      </Table>
    </Section>
  );
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'good' | 'bad';
}): JSX.Element {
  return (
    <div className="rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface-muted)] px-3 py-2">
      <div className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-ink-muted)]">
        {label}
      </div>
      <div
        className={`text-[17px] font-semibold tabular-nums ${
          tone === 'bad'
            ? 'text-red-700 dark:text-red-400'
            : tone === 'good'
              ? 'text-emerald-700 dark:text-emerald-400'
              : ''
        }`}
      >
        {value}
      </div>
    </div>
  );
}
